import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ActionCard } from '~/eds/src/components/Card/subcomponents/ActionCard';
import { CardsGrid } from '~/eds/src/components/Card/subcomponents/CardsGrid';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { api } from '~/redux/api/createApi';

type Props = {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string | null) => void;
};

export const TemplateStep = ({
  selectedTemplateId,
  onSelectTemplate,
}: Props) => {
  const intl = useIntl();

  const {
    data: templatesResponse,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
    refetch,
  } = api.endpoints.getPlaybookTemplates.useQuery(undefined);

  const templates = useMemo(
    () =>
      templatesResponse?.data?.map((t) => ({
        ...t.attributes,
        id: t.id,
      })) ?? [],
    [templatesResponse],
  );

  const handleToggleTemplate = (templateId: string) => {
    onSelectTemplate(selectedTemplateId === templateId ? null : templateId);
  };

  return (
    <Layout direction="column" spacing={4} flex="1">
      <ContentContainer
        loadingContent={{ isLoading: isLoadingTemplates }}
        placeholderContent={
          isTemplatesError
            ? {
                icon: 'status-danger',
                title: intl.formatMessage({
                  id: 'TemplateStep.errorTitle',
                  defaultMessage: 'Failed to load templates',
                  description: 'Error title when templates fail to load',
                }),
                description: intl.formatMessage({
                  id: 'TemplateStep.errorDescription',
                  defaultMessage: 'Something went wrong. Please try again.',
                  description: 'Error description when templates fail to load',
                }),
                action: {
                  text: intl.formatMessage({
                    id: 'TemplateStep.retryLabel',
                    defaultMessage: 'Retry',
                    description:
                      'Retry button label when templates fail to load',
                  }),
                  onClick: refetch,
                },
              }
            : undefined
        }
      >
        <CardsGrid
          cards={templates}
          gridOptions={{
            columns: 3,
            columnWidths: ['32%', '32%', '32%'],
            width: '100%',
          }}
          renderItem={(template) => (
            <TemplateCard
              key={template.id}
              id={template.id}
              name={template.name}
              description={template.short_description}
              isSelected={selectedTemplateId === template.id}
              onSelect={() => handleToggleTemplate(template.id)}
            />
          )}
        />
      </ContentContainer>
    </Layout>
  );
};

type TemplateCardProps = {
  id: string;
  name: string;
  description: string;
  isSelected: boolean;
  onSelect: () => void;
};

const TemplateCard = ({
  id,
  name,
  description,
  isSelected,
  onSelect,
}: TemplateCardProps) => {
  const intl = useIntl();

  const actions = isSelected
    ? [
        {
          icon: 'circle-check' as const,
          iconPosition: 'left' as const,
          level: 'secondary' as const,
          mode: 'chip' as const,
          status: 'success' as const,
          text: intl.formatMessage({
            id: 'TemplateStep.selectedLabel',
            defaultMessage: 'Selected',
            description: 'Label shown when a template is selected',
          }),
          onClick: onSelect,
        },
      ]
    : [
        {
          icon: 'plus-circle' as const,
          iconPosition: 'left' as const,
          level: 'secondary' as const,
          mode: 'chip' as const,
          status: 'active' as const,
          text: intl.formatMessage({
            id: 'TemplateStep.selectTemplateLabel',
            defaultMessage: 'Select Template',
            description: 'Button text to select a playbook template',
          }),
          onClick: onSelect,
        },
      ];

  return (
    <ActionCard
      actions={actions}
      description={description}
      descriptionLimit={3}
      icon={{ icon: 'file', color: 'currentColor' }}
      height="156px"
      name={`template-card-${id}`}
      size="m"
      title={name}
      titleLimit={2}
      onClick={onSelect}
    />
  );
};
